import React from 'react';
import { formatTimeMs } from '../game/leaderboard';
import type { GameMode, BlitzEndReason, VersusTeam } from '../game/types';

interface GameOverModalProps {
  mode?: GameMode;
  finalTimeMs?: number;
  baseTimeMs?: number;
  penaltySeconds?: number;
  hintsUsed?: number;
  blitzSetsFound?: number;
  blitzTimeLeftMs?: number;
  blitzEndReason?: BlitzEndReason;
  versusWinner?: VersusTeam | 'tie' | null;
  versusBlueScore?: number;
  versusRedScore?: number;
  versusReason?: string;
  onlineWinner?: 'self' | 'opponent' | 'tie' | null;
  onlineMyScore?: number;
  onlineOpponentScore?: number;
  onlineReason?: string;
  rematchRequestedByPeer?: boolean;
  rematchSent?: boolean;
  rank: number | null;
  onPlayAgain: () => void;
  onOpenLeaderboard?: () => void;
  onBackMenu: () => void;
}

export const GameOverModal: React.FC<GameOverModalProps> = ({
  mode = 'classic',
  finalTimeMs = 0,
  baseTimeMs = 0,
  penaltySeconds = 0,
  hintsUsed = 0,
  blitzSetsFound = 0,
  blitzTimeLeftMs = 0,
  blitzEndReason = 'timeout',
  versusWinner = 'tie',
  versusBlueScore = 0,
  versusRedScore = 0,
  versusReason = '',
  onlineWinner = null,
  onlineMyScore = 0,
  onlineOpponentScore = 0,
  onlineReason = '',
  rematchRequestedByPeer = false,
  rematchSent = false,
  rank,
  onPlayAgain,
  onOpenLeaderboard,
  onBackMenu,
}) => {
  const isBlitz = mode === 'blitz';
  const isVersus = mode === 'versus';
  const isOnlineClash = mode === 'online_clash';
  const isOnlineRace = mode === 'online_race';
  const isOnlineBlitz = mode === 'online_blitz';
  const isOnline = isOnlineClash || isOnlineRace || isOnlineBlitz;

  const getTitle = () => {
    if (isOnlineClash) {
      if (onlineWinner === 'self') return 'Victory! 🏆';
      if (onlineWinner === 'opponent') return 'Defeat ⚔️';
      return "It's a Tie!";
    }
    if (isOnlineRace) {
      if (onlineWinner === 'self') return 'You Won the Race! 🏁';
      return 'Opponent Finished First! ⏱️';
    }
    if (isOnlineBlitz) {
      if (onlineWinner === 'self') return 'Blitz Victory! ⚡';
      return 'Blitz Knockout! 💥';
    }
    if (isVersus) {
      if (versusWinner === 'blue') return 'Blue Team Wins!';
      if (versusWinner === 'red') return 'Red Team Wins!';
      return "It's a Tie!";
    }
    if (isBlitz) return 'Blitz Complete';
    return 'Game Complete!';
  };

  return (
    <div className="modal-overlay" onClick={onBackMenu}>
      <div className="modal-content victory-content" onClick={(e) => e.stopPropagation()}>
        <h2>{getTitle()}</h2>

        {isOnlineClash ? (
          <>
            <div className={`versus-winner-badge ${onlineWinner === 'self' ? 'badge-blue' : onlineWinner === 'opponent' ? 'badge-red' : 'badge-tie'}`}>
              {onlineWinner === 'self' ? 'YOU WON' : onlineWinner === 'opponent' ? 'OPPONENT WON' : 'TIE GAME'}
            </div>
            <div className="victory-subtitle">
              {onlineReason || (onlineWinner === 'self' ? 'Reached 14 sets!' : 'Match concluded!')}
            </div>
          </>
        ) : isOnlineRace ? (
          <>
            <div className={`versus-winner-badge ${onlineWinner === 'self' ? 'badge-blue' : 'badge-red'}`}>
              {onlineWinner === 'self' ? 'FIRST TO FINISH' : 'RUNNER UP'}
            </div>
            <div className="victory-subtitle">
              {onlineReason || (onlineWinner === 'self' ? 'You completed all sets first!' : 'Opponent completed all sets first!')}
            </div>
          </>
        ) : isOnlineBlitz ? (
          <>
            <div className={`versus-winner-badge ${onlineWinner === 'self' ? 'badge-blue' : 'badge-red'}`}>
              {onlineWinner === 'self' ? 'VICTORY' : 'ELIMINATED'}
            </div>
            <div className="victory-subtitle">
              {onlineReason || (onlineWinner === 'self' ? 'Surviving player standing!' : 'Knocked out!')}
            </div>
          </>
        ) : isVersus ? (
          <>
            <div className={`versus-winner-badge badge-${versusWinner}`}>
              {versusWinner === 'tie' ? 'TIE GAME' : `${versusWinner?.toUpperCase()} VICTORY`}
            </div>
            <div className="victory-subtitle">
              {versusReason || (versusWinner === 'tie' ? 'Equal sets found!' : 'Match completed!')}
            </div>
          </>
        ) : isBlitz ? (
          <>
            <div className={`blitz-result-badge badge-${blitzEndReason}`}>
              {blitzEndReason === 'cleared' && 'DECK CLEARED'}
              {blitzEndReason === 'timeout' && 'TIME UP'}
              {blitzEndReason === 'mistake' && 'MISTAKE'}
            </div>
            <div className="victory-subtitle">
              {blitzEndReason === 'cleared' && 'All available sets cleared!'}
              {blitzEndReason === 'timeout' && 'Time ran out!'}
              {blitzEndReason === 'mistake' && 'Invalid set selected!'}
            </div>
          </>
        ) : (
          <div className="victory-subtitle">All possible sets found!</div>
        )}

        {!isVersus && !isOnline && rank !== null && (
          <div className="rank-alert">★ Ranked #{rank} Personal Best!</div>
        )}

        <div className="victory-stats">
          {isOnlineClash ? (
            <div className="versus-final-score-row">
              <div className="versus-final-col col-blue">
                <span className="versus-team-title">YOU (BLUE)</span>
                <strong className="versus-score-number">{onlineMyScore}</strong>
                <span className="versus-score-unit">sets</span>
              </div>
              <span className="versus-score-dash">:</span>
              <div className="versus-final-col col-red">
                <span className="versus-team-title">OPPONENT (RED)</span>
                <strong className="versus-score-number">{onlineOpponentScore}</strong>
                <span className="versus-score-unit">sets</span>
              </div>
            </div>
          ) : isOnlineRace ? (
            <div className="race-final-stats">
              <div className="stat-line">
                <span>Your Sets Found:</span>
                <strong>{onlineMyScore}</strong>
              </div>
              <div className="stat-line">
                <span>Opponent Sets Found:</span>
                <strong>{onlineOpponentScore}</strong>
              </div>
              {finalTimeMs > 0 && (
                <div className="stat-line">
                  <span>Your Time:</span>
                  <strong>{formatTimeMs(finalTimeMs)}</strong>
                </div>
              )}
            </div>
          ) : isOnlineBlitz ? (
            <div className="race-final-stats">
              <div className="stat-line">
                <span>Your Sets Found:</span>
                <strong>{onlineMyScore}</strong>
              </div>
              <div className="stat-line">
                <span>Opponent Sets Found:</span>
                <strong>{onlineOpponentScore}</strong>
              </div>
            </div>
          ) : isVersus ? (
            <div className="versus-final-score-row">
              <div className="versus-final-col col-blue">
                <span className="versus-team-title">BLUE TEAM</span>
                <strong className="versus-score-number">{versusBlueScore}</strong>
                <span className="versus-score-unit">sets</span>
              </div>
              <span className="versus-score-dash">:</span>
              <div className="versus-final-col col-red">
                <span className="versus-team-title">RED TEAM</span>
                <strong className="versus-score-number">{versusRedScore}</strong>
                <span className="versus-score-unit">sets</span>
              </div>
            </div>
          ) : isBlitz ? (
            <>
              <div className="stat-line">
                <span>Sets Found:</span>
                <strong>{blitzSetsFound}</strong>
              </div>
              <div className="stat-line">
                <span>Time Remaining:</span>
                <strong>{formatTimeMs(blitzTimeLeftMs)}</strong>
              </div>
            </>
          ) : (
            <>
              <div className="stat-line">
                <span>Final Time:</span>
                <strong>{formatTimeMs(finalTimeMs)}</strong>
              </div>
              <div className="stat-sub">
                Base Time: {formatTimeMs(baseTimeMs)}
                {penaltySeconds > 0 && ` | +${penaltySeconds}s (${hintsUsed} hints)`}
              </div>
            </>
          )}
        </div>

        {rematchRequestedByPeer && (
          <div className="rematch-banner">
            ⚔️ Opponent requested a rematch! Tap below to accept.
          </div>
        )}

        <div className="modal-actions victory-actions">
          <button
            className="btn btn-primary btn-full-width"
            id="btn-play-again"
            onClick={() => onPlayAgain()}
            disabled={rematchSent}
          >
            {isOnline
              ? rematchSent
                ? 'Waiting for Opponent...'
                : rematchRequestedByPeer
                ? 'Accept Rematch'
                : 'Rematch'
              : isVersus
              ? 'Rematch'
              : 'Play Again'}
          </button>
          <div className="victory-secondary-buttons">
            {!isVersus && !isOnline && onOpenLeaderboard && (
              <button className="btn btn-secondary" onClick={onOpenLeaderboard}>
                Leaderboard
              </button>
            )}
            <button className="btn btn-secondary" id="btn-menu" onClick={onBackMenu}>
              Main Menu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
